import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  Tooltip, 
  IconButton, 
  Chip,
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit, 
  Delete, 
  DeleteForever 
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';


const CompetencyFrameworks = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canCreate = hasPermission(user?.permissions, 'CompetencyFrameworks', 'create', false);
  const canDelete = hasPermission(user?.permissions, 'CompetencyFrameworks', 'delete', false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: paginationModel.page + 1, limit: paginationModel.pageSize };
      const res = await api.get('/competencyframeworks', { params });
      const d = res.data;
      setRows(d.data || d);
      setTotal(d.total || (d.data || d).length);
    } catch (err) {
      setError(err);
      toast.error('Failed to load frameworks!');
    } finally {
      setLoading(false);
    }
  }, [paginationModel]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.patch('/competencyframeworks/soft-delete', { ids: [idToDelete] });
        setRows((prev) => prev.filter((r) => r._id !== idToDelete));
        toast.success('Framework deleted.');
      } else {
        const ids = getSelectedIds(rowSelectionModel, rows);
        if (!ids.length) { toast.error('No rows selected!'); return; }
        await api.patch('/competencyframeworks/soft-delete', { ids });
        setRows((prev) => prev.filter((r) => !ids.includes(r._id)));
        toast.success(`${ids.length} framework(s) deleted.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed!');
    }
  }, [deleteType, idToDelete, rowSelectionModel, rows]);

  const columns = useMemo(() => [
    {
      field: 'no', headerName: '#', width: 60, sortable: false, filterable: false,
      renderCell: (p) => p.api.getAllRowIds().indexOf(p.id) + 1,
    },
    {
      field: 'code', headerName: 'Code',
      ...(() => isMobile ? { width: 100 } : { flex: 1.5 })(),
    },
    {
      field: 'name', headerName: 'Name',
      ...(() => isMobile ? { width: 200 } : { flex: 3 })(),
    },
    {
      field: 'levels', headerName: 'Levels',
      ...(() => isMobile ? { width: 120 } : { flex: 2 })(),
      renderCell: ({ value }) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {(value || []).map((l) => (
            <Chip key={l.level} label={l.label} size="small" sx={{ bgcolor: l.color, color: '#fff', fontSize: '0.65rem' }} />
          ))}
        </Box>
      ),
    },
    {
      field: 'regulatoryRef', headerName: 'Regulatory Ref',
      ...(() => isMobile ? { width: 140 } : { flex: 2 })(),
    },
    {
      field: 'isActive', headerName: 'Active',
      ...(() => isMobile ? { width: 80 } : { flex: 1 })(),
      renderCell: ({ value }) => (
        <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />
      ),
    },
  ], [isMobile]);

  const actionColumn = [{
    field: 'action', 
    headerName: 'Action', 
    sortable: false, 
    filterable: false,
    width: canDelete ? 125 : 80,
    renderCell: (params) => (
      <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
        <Tooltip title="Edit">
          <IconButton
            sx={{ color: primaryDark, transition: 'color 0.3s', '&:hover': { color: greenDeep } }}
            onClick={() => navigate(`/competencyframeworks/${params.row._id}`)}
          >
            <Edit />
          </IconButton>
        </Tooltip>
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: primaryDark, transition: 'color 0.3s', '&:hover': { color: greenDeep } }}
              onClick={() => { setIdToDelete(params.row._id); setDeleteType('single'); setConfirmDialogOpen(true); }}
            >
              <DeleteForever />
            </IconButton>
          </Tooltip>
        )}
      </FlexCenter>
    ),
  }];

  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header title="Competency Frameworks" subtitle="EASA Evidence-Based Training" />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Bulk delete" arrow>
              <span>
                <StyledButton
                  disabled={!canDelete || !isAnyRowSelected(rowSelectionModel, rows)}
                  onClick={() => { setDeleteType('bulk'); setConfirmDialogOpen(true); }}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
            <Tooltip title="Create" arrow>
              <span>
                <StyledButton disabled={!canCreate} onClick={() => navigate('/competencyframeworks/new')}>
                  <AddCircleOutline />
                </StyledButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>

      {error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: 'span 12', overflowY: 'auto' }}>
            {isMobile ? (
              <CardList
                rows={rows}
                primaryField="code"
                secondaryField="name"
                fields={[
                  { label: "Regulatory Ref", key: "regulatoryRef" },
                ]}
                canEdit
                canDelete={canDelete}
                onEdit={(id) => navigate(`/competencyframeworks/${id}`)}
                onDelete={(id) => { setIdToDelete(id); setDeleteType('single'); setConfirmDialogOpen(true); }}
              />
            ) : (
              <DataGridComponent
                rows={rows}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  rowCount: total,
                  paginationMode: "server",
                  paginationModel,
                  onPaginationModelChange: setPaginationModel,
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  rowSelectionModel,
                  onRowSelectionModelChange: setRowSelectionModel,
                  getRowHeight: () => 'auto',
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default CompetencyFrameworks;
